import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  useBorrarLeccion, useGuardarLeccion,
  type CursoDetalle, type Leccion, type TipoDeLeccion,
} from '../api';

/**
 * Lecciones — agregar, editar y borrar las lecciones de un curso.
 *
 * ## El PDF que se subía en la lección equivocada
 *
 * El archivo elegido vivía en un objeto del módulo que **sobrevivía al cierre
 * del formulario**, así que un PDF elegido y descartado se subía en la siguiente
 * lección que alguien guardara. Hasta el 2026-08-28 vivía en
 * `window._alPdfFile`. Aquí nace y muere con el formulario.
 *
 * ## Si el PDF falla, la lección ya existe
 *
 * Se dice así —«se guardó, pero el PDF no»— y el formulario se cierra. Dejarlo
 * abierto con los mismos datos invitaba a pulsar otra vez, y eso creaba una
 * segunda lección con el mismo título.
 */

/** El límite que aplica el servidor al PDF de una lección. */
const MAX_PDF_MB = 50;

export function Lecciones({ curso }: { curso: CursoDetalle }) {
  const [editando, setEditando] = useState<Leccion | 'nueva' | null>(null);
  const [borrando, setBorrando] = useState<Leccion | null>(null);
  const borrar = useBorrarLeccion();

  if (editando) {
    return (
      <FormularioDeLeccion
        curso={curso}
        leccion={editando === 'nueva' ? null : editando}
        onCerrar={() => setEditando(null)}
      />
    );
  }

  const lecciones = curso.lessons;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditando('nueva')}>
          <i className="bi bi-plus-circle" aria-hidden="true" /> Agregar lección
        </Button>
      </div>

      {!lecciones.length ? (
        <EstadoVacio
          icono="bi-collection-play"
          titulo="Sin lecciones"
          descripcion="Agrega la primera para que el curso tenga contenido."
          accion={{ texto: 'Agregar lección', onClick: () => setEditando('nueva') }}
          compacto
        />
      ) : (
        <ol className="flex flex-col gap-2">
          {lecciones.map((l, i) => (
            <li key={l.id} className="flex items-center gap-3 rounded-sm border border-line bg-sunken p-3">
              <span className="w-5 text-center font-bold tabular-nums text-muted" aria-hidden="true">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-3 font-semibold text-ink">{l.title}</p>
                <p className="flex items-center gap-1.5 text-2 text-muted">
                  <i className={cn('bi', l.video_type === 'pdf' ? 'bi-file-earmark-pdf' : 'bi-youtube')} aria-hidden="true" />
                  {l.video_type === 'pdf' ? 'PDF' : 'YouTube'}
                  {l.duration_minutes ? ` · ${l.duration_minutes} min` : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" aria-label={`Editar la lección ${l.title}`} onClick={() => setEditando(l)}>
                <i className="bi bi-pencil" aria-hidden="true" />
              </Button>
              <Button variant="ghost" size="sm" aria-label={`Eliminar la lección ${l.title}`} onClick={() => setBorrando(l)}>
                <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ol>
      )}

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar la lección"
        descripcion={borrando ? `«${borrando.title}» y sus preguntas del quiz se retiran del curso.` : ''}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(
            { cursoId: curso.id, leccionId: borrando.id },
            {
              onSuccess: () => { setBorrando(null); Utils.toast('Lección eliminada', 'info'); },
              onError: (err) => {
                setBorrando(null);
                Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
              },
            },
          );
        }}
      />
    </div>
  );
}

function FormularioDeLeccion({
  curso, leccion, onCerrar,
}: {
  curso: CursoDetalle;
  leccion: Leccion | null;
  onCerrar: () => void;
}) {
  const guardar = useGuardarLeccion();
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState<TipoDeLeccion>('youtube');
  const [url, setUrl] = useState('');
  const [duracion, setDuracion] = useState('');
  const [orden, setOrden] = useState('');
  const [pdf, setPdf] = useState<File | null>(null);
  const [errores, setErrores] = useState<Record<string, string>>({});

  useEffect(() => {
    setTitulo(leccion?.title ?? '');
    setDescripcion(leccion?.description ?? '');
    setTipo(leccion?.video_type === 'pdf' ? 'pdf' : 'youtube');
    setUrl(leccion?.video_url ?? '');
    setDuracion(leccion?.duration_minutes != null ? String(leccion.duration_minutes) : '');
    setOrden(String(leccion?.order ?? curso.lessons.length));
    setPdf(null);
    setErrores({});
  }, [leccion, curso.lessons.length]);

  function elegirPdf(entrada: HTMLInputElement) {
    const f = entrada.files?.[0];
    if (!f) { setPdf(null); return; }
    if (f.size > MAX_PDF_MB * 1024 * 1024) {
      Utils.toast(`«${f.name}» pesa más de ${MAX_PDF_MB} MB`, 'warning');
      // Fuera también el que hubiera antes. Si no, el campo enseñaba el
      // rechazado y al guardar se subía el anterior, sin que nadie lo supiera.
      setPdf(null);
      entrada.value = '';
      return;
    }
    setPdf(f);
    setErrores((x) => ({ ...x, pdf: '' }));
  }

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    const e2: Record<string, string> = {};
    if (!titulo.trim()) e2.titulo = 'La lección necesita un título.';
    // También al editar: una lección de YouTube sin dirección es una lección vacía.
    if (tipo === 'youtube' && !url.trim()) e2.url = 'Pega la dirección del vídeo de YouTube.';
    if (!leccion && tipo === 'pdf' && !pdf) e2.pdf = 'Elige el PDF de la lección.';
    setErrores(e2);
    if (Object.keys(e2).length) return;

    guardar.mutate(
      {
        cursoId: curso.id,
        leccionId: leccion?.id ?? null,
        cuerpo: {
          title: titulo.trim(),
          // En el alta, `null` es «sin dato». En la edición el servidor lee
          // `null` como «no lo toques» y vacía con `""` y con `0` —«0 significa
          // sin duración declarada», dice `update_lesson`—. El vanilla mandaba
          // `null` también al editar, así que una descripción o una duración,
          // una vez puestas, no se podían borrar.
          description: leccion ? descripcion.trim() : descripcion.trim() || null,
          duration_minutes: duracion === '' ? (leccion ? 0 : null) : Number(duracion),
          order: Number(orden) || 0,
          ...(leccion ? {} : { video_type: tipo }),
          ...(tipo === 'youtube' ? { video_url: url.trim() || null } : {}),
        },
        pdf: tipo === 'pdf' ? pdf : null,
      },
      {
        onSuccess: ({ errorDelPdf }) => {
          if (errorDelPdf) {
            const motivo = errorDelPdf instanceof Error ? errorDelPdf.message : 'error desconocido';
            Utils.toast(
              `${leccion ? 'Los cambios se guardaron' : 'La lección se creó'}, pero el PDF no se subió (${motivo}). ` +
                'Puedes volver a intentarlo editándola.',
              'warning',
              8000,
            );
          } else {
            Utils.toast(leccion ? 'Lección actualizada' : 'Lección agregada', 'success');
          }
          onCerrar();
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo guardar la lección', 'error'),
      },
    );
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4">
      <div>
        <Button type="button" variant="ghost" size="sm" onClick={onCerrar}>
          <i className="bi bi-arrow-left" aria-hidden="true" /> Volver a las lecciones
        </Button>
      </div>

      <Field label="Título" error={errores.titulo}>
        <Input
          value={titulo}
          maxLength={200}
          onChange={(e) => { setTitulo(e.target.value); setErrores((x) => ({ ...x, titulo: '' })); }}
          placeholder="Ej: Introducción al módulo"
        />
      </Field>

      <Field label="Descripción" opcional>
        <Textarea rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </Field>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Tipo de contenido" ayuda={leccion ? 'No se cambia al editar.' : undefined}>
          <Select value={tipo} disabled={!!leccion} onChange={(e) => setTipo(e.target.value as TipoDeLeccion)}>
            <option value="youtube">Vídeo de YouTube</option>
            <option value="pdf">PDF o presentación</option>
          </Select>
        </Field>
        <Field label="Duración (min)" opcional>
          <Input type="number" min={1} value={duracion} onChange={(e) => setDuracion(e.target.value)} placeholder="Ej: 15" />
        </Field>
        <Field label="Orden">
          <Input type="number" min={0} value={orden} onChange={(e) => setOrden(e.target.value)} />
        </Field>
      </div>

      {tipo === 'youtube' ? (
        <Field
          label="Dirección de YouTube"
          error={errores.url}
          ayuda="Solo se admiten vídeos de YouTube: de la dirección se usa el identificador del vídeo."
        >
          <Input
            type="url"
            value={url}
            onChange={(e) => { setUrl(e.target.value); setErrores((x) => ({ ...x, url: '' })); }}
            placeholder="https://www.youtube.com/watch?v=…"
          />
        </Field>
      ) : (
        <Field
          label={leccion ? 'Reemplazar el PDF' : 'Archivo PDF'}
          opcional={!!leccion}
          error={errores.pdf}
          ayuda={`Hasta ${MAX_PDF_MB} MB.`}
        >
          <Input type="file" accept=".pdf,application/pdf" onChange={(e) => elegirPdf(e.currentTarget)} />
        </Field>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCerrar}>Cancelar</Button>
        <Button type="submit" loading={guardar.isPending}>
          <i className="bi bi-floppy" aria-hidden="true" /> {leccion ? 'Guardar cambios' : 'Agregar lección'}
        </Button>
      </div>
    </form>
  );
}
